import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { useRangeCalendar } from '@react-aria/calendar';
import { useDateField, useDateRangePicker } from '@react-aria/datepicker';
import { useLocale } from '@react-aria/i18n';
import { useRangeCalendarState } from '@react-stately/calendar';
import { useDateFieldState, useDateRangePickerState } from '@react-stately/datepicker';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  calendarTitleRange,
  convertAriaDateRangeToDateRange,
  convertAriaDateToDate,
  convertDateRangeToAriaDateRange,
  convertDateToAriaDateTime,
  createCalendar,
  getDateValidation,
} from '../../../utils';

import type { DateValue } from '@internationalized/date';
import type { RefObject } from 'react';

import type { DateRangeValue } from '../../../utils';
import type { DateTimeRangePickerProps, UseDateTimeRangePickerReturn } from './date-time-range-picker.types';

/**
 * Custom hook that manages the state and behavior for the DateTimeRangePicker component.
 * Combines React Aria's date range picker functionality with time selection capabilities.
 *
 * This hook handles:
 * - Date range picker state management via React Aria
 * - Start and end date/time input states
 * - Calendar state for the popup
 * - Time selection integration
 * - Focus management and keyboard navigation
 * - Form integration with hidden inputs
 *
 * @param props - DateTimeRangePicker component props
 * @param ref - React ref to the container element
 * @returns Object containing state, props, and handlers for the component
 */
export const useDateTimeRangePicker = (
  props: DateTimeRangePickerProps,
  ref: RefObject<Element>,
): UseDateTimeRangePickerReturn => {
  const {
    defaultValue,
    disabled,
    form,
    granularity = 'minute',
    helperText,
    hourCycle = 12,
    id,
    label,
    locale: localeProp,
    maxDate,
    minDate,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onKeyUp,
    onOpenChange,
    placeholderValue,
    readOnly,
    required,
    showTimezone = false,
    value,
  } = props;

  const { locale: defaultLocale } = useLocale();
  const locale = localeProp ?? defaultLocale;

  // Memoize converted date values to prevent infinite re-render loops.
  // convertDateToAriaDateTime / convertDateRangeToAriaDateRange create new objects on every call,
  // and React Aria's state hooks trigger onChange when they receive a new value reference, causing
  // a render cascade: new ref → state update → onChange → setValue → new render.
  const ariaValue = useMemo(
    () => convertDateRangeToAriaDateRange(value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value?.start?.getTime(), value?.end?.getTime()],
  );
  const ariaDefaultValue = useMemo(
    () => convertDateRangeToAriaDateRange(defaultValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultValue?.start?.getTime(), defaultValue?.end?.getTime()],
  );
  const ariaMinValue = useMemo(
    () => convertDateToAriaDateTime(minDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [minDate?.getTime()],
  );
  const ariaMaxValue = useMemo(
    () => convertDateToAriaDateTime(maxDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxDate?.getTime()],
  );
  const ariaPlaceholderValue = useMemo(
    () => convertDateToAriaDateTime(placeholderValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placeholderValue?.getTime()],
  );

  // Initialize the main date range picker state using React Aria
  const state = useDateRangePickerState({
    defaultValue: ariaDefaultValue,
    granularity,
    hideTimeZone: !showTimezone,
    hourCycle,
    isDisabled: disabled,
    isReadOnly: readOnly,
    isRequired: required,
    maxValue: ariaMaxValue,
    minValue: ariaMinValue,
    onBlur,
    onChange: (newValue) => {
      let newDateRange = convertAriaDateRangeToDateRange(newValue);

      // Swap end and start if end is before start
      if (newDateRange?.end && newDateRange.start && newDateRange.end < newDateRange.start) {
        newDateRange = {
          end: newDateRange.start,
          start: newDateRange.end,
        };
      }

      if (newDateRange) {
        // Perform validation on the new date range
        const validationResult = getDateValidation(newDateRange.start, props, locale);

        // Call the user's onChange handler with the date range and validation context
        onChange?.(newDateRange, {
          valid: validationResult.validityState.valid,
          ...validationResult,
        });
      } else {
        // Handle null/cleared range
        onChange?.(null, {
          valid: !required, // Valid if not required, invalid if required
          validationMessage: required ? ['Range is required'] : [],
          validityState: {
            badInput: false,
            customError: false,
            patternMismatch: false,
            rangeOverflow: false,
            rangeUnderflow: false,
            stepMismatch: false,
            tooLong: false,
            tooShort: false,
            typeMismatch: false,
            valid: !required,
            valueMissing: required || false,
          },
        });
      }
    },
    onFocus,
    onKeyDown,
    onKeyUp,
    onOpenChange,
    placeholderValue: ariaPlaceholderValue,
    shouldCloseOnSelect: false,
    validationBehavior: 'aria',
    value: ariaValue,
  });

  // Get React Aria props for accessibility and form integration
  const useDateRangePickerReturn = useDateRangePicker(
    {
      'aria-label': typeof label === 'string' ? label : undefined,
      description: typeof helperText === 'string' ? helperText : undefined,
      endName: 'end-date-range-picker-input',
      form,
      granularity,
      hideTimeZone: !showTimezone,
      hourCycle,
      id: id ?? undefined,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      label: typeof label === 'string' ? label : undefined,
      maxValue: ariaMaxValue,
      minValue: ariaMinValue,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onOpenChange,
      placeholderValue: ariaPlaceholderValue,
      shouldForceLeadingZeros: true,
      startName: 'start-date-range-picker-input',
      validationBehavior: 'aria',
    },
    state,
    ref,
  );

  const {
    calendarProps: _calendarProps,
    dialogProps,
    endFieldProps,
    groupProps,
    startFieldProps,
  } = useDateRangePickerReturn;

  // Date input related props
  const dateFieldStartRef = useRef(null);
  const dateFieldStartInputRef = useRef(null);
  const dateFieldEndRef = useRef(null);
  const dateFieldEndInputRef = useRef(null);

  const dateFieldStartState = useDateFieldState({
    ...startFieldProps,
    createCalendar,
    locale,
    maxValue: ariaMaxValue,
    minValue: ariaMinValue,
    value: state.value?.start,
  });
  const dateFieldEndState = useDateFieldState({
    ...endFieldProps,
    createCalendar,
    locale,
    maxValue: ariaMaxValue,
    minValue: ariaMinValue,
    value: state.value?.end,
  });

  const useDateFieldStartReturn = useDateField(
    {
      ...startFieldProps,
      'aria-label': typeof label === 'string' ? `${label} start` : undefined,
      description: typeof helperText === 'string' ? helperText : undefined,
      inputRef: dateFieldStartInputRef,
      label: typeof label === 'string' ? `${label} start` : undefined,
    },
    dateFieldStartState,
    dateFieldStartRef,
  );
  const useDateFieldEndReturn = useDateField(
    {
      ...endFieldProps,
      'aria-label': typeof label === 'string' ? `${label} end` : undefined,
      description: typeof helperText === 'string' ? helperText : undefined,
      inputRef: dateFieldEndInputRef,
      label: typeof label === 'string' ? `${label} end` : undefined,
    },
    dateFieldEndState,
    dateFieldEndRef,
  );

  const { fieldProps: dateFieldStartProps, inputProps: startInputProps } = useDateFieldStartReturn;
  const { fieldProps: dateFieldEndProps, inputProps: endInputProps } = useDateFieldEndReturn;

  const hiddenInputStartProps = {
    ...startInputProps,
    disabled,
    readOnly,
    required,
  };
  const hiddenInputEndProps = {
    ...endInputProps,
    disabled,
    readOnly,
    required,
  };

  // Calendar related props
  const initialFocusedValue = state.value?.start ?? state.value?.end ?? today(getLocalTimeZone());
  const [focusedValue, setFocusedValue] = useState(initialFocusedValue);

  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarState = useRangeCalendarState({
    ..._calendarProps,
    createCalendar,
    focusedValue,
    locale,
    onChange: (value) => {
      _calendarProps?.onChange?.(value);

      const dateRange = {
        end: value?.end ?? null,
        start: value?.start ?? null,
      };

      if (dateRange.start instanceof CalendarDate) {
        const date = dateRange.start.toDate(getLocalTimeZone());
        dateRange.start = convertDateToAriaDateTime(date);
      }

      if (dateRange.end instanceof CalendarDate) {
        const date = dateRange.end.toDate(getLocalTimeZone());
        // Set the end time to the end of the day
        dateRange.end = convertDateToAriaDateTime(date).set({
          hour: granularity === 'hour' || granularity === 'minute' || granularity === 'second' ? 23 : 0,
          minute: granularity === 'minute' || granularity === 'second' ? 59 : 0,
          second: granularity === 'second' ? 59 : 0,
        });
      }

      state.setValue(dateRange);
    },
    onFocusChange: (date) => {
      _calendarProps?.onFocusChange?.(date);
      if (date) {
        setFocusedValue(date);
      }
    },
    visibleDuration: { months: 2 },
  });

  useEffect(() => {
    if (state.value?.end) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setFocusedValue(state.value.end as CalendarDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value?.end?.toString()]);

  useEffect(() => {
    if (state.value?.start) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setFocusedValue(state.value.start as CalendarDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value?.start?.toString()]);

  const useRangeCalendarReturn = useRangeCalendar({ ..._calendarProps }, calendarState, calendarRef);
  const {
    calendarProps,
    nextButtonProps: _nextButtonProps,
    prevButtonProps: _prevButtonProps,
  } = useRangeCalendarReturn;

  const [leftTitle, rightTitle] = calendarTitleRange(calendarState.visibleRange);

  const nextButtonProps = {
    'aria-label': 'Next month',
    disabled: _nextButtonProps.isDisabled,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      calendarState.focusNextPage();
      e.stopPropagation();
    },
  };

  const prevButtonProps = {
    'aria-label': 'Previous month',
    disabled: _prevButtonProps.isDisabled,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      calendarState.focusPreviousPage();
      e.stopPropagation();
    },
  };

  // Time management for the selected date/time
  const timeValue = {
    end: state.value?.end ? convertAriaDateToDate(state.value.end) : null,
    start: state.value?.start ? convertAriaDateToDate(state.value.start) : null,
  };

  const handleTimeChange = (part: 'end' | 'start', newValue: Date): void => {
    const newAriaDate = convertDateToAriaDateTime(newValue);
    state.setValue({
      ...state.value,
      [part]: newAriaDate,
    } as DateRangeValue<DateValue>);
  };

  return {
    calendarProps: {
      calendarProps,
      calendarRef,
      calendarState,
      leftTitle,
      nextButtonProps,
      prevButtonProps,
      rightTitle,
    },
    dateFieldProps: {
      dateFieldEndInputRef,
      dateFieldEndProps,
      dateFieldEndRef,
      dateFieldEndState,
      hiddenInputEndProps,

      dateFieldStartInputRef,
      dateFieldStartProps,
      dateFieldStartRef,
      dateFieldStartState,
      hiddenInputStartProps,

      groupProps,
    },
    dateRangePickerState: state,
    popupProps: {
      dialogProps,
    },
    timeFieldProps: {
      onTimeChange: handleTimeChange,
      timeValue,
    },
  };
};
